/**
 * CoSingery — 全 app **單一播放器**（context ＋ 一個 `HTMLAudioElement`）
 *
 * 為何要放喺 app 層：用戶想「邊行邊聽」—— 由「我嘅歌」揀一首，去對話頁、去播放器頁都要**繼續播**。
 *   → 一定要**一個** audio element 住 app shell，唔可以每個畫面自己一個（會有兩首歌同時播）。
 *
 * 誠實原則（`rules.md` B2／B5）：
 *   · 播放失敗 → 記落 `error`（原樣顯示），**唔會**扮播緊
 *   · 未揀 flac 就唔提供 flac（`hifiSrc === null`）
 *
 * 額外：支援 **Media Session API**（鎖屏／耳機掣控制下一首）—— 手機體驗必要。
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { clampSeek, pickNextIndex, pickPrevIndex, type RepeatMode, type Track } from './playerModel';

export interface PlayerApi {
  track: Track | null;
  queue: Track[];
  playing: boolean;
  positionMs: number;
  durationMs: number | null;
  error: string | null;
  hifi: boolean;
  /** ⭐ 2026-09-24：播放模式（產品負責人：「repeat one, repeat all, random play etc」） */
  repeat: RepeatMode;
  shuffle: boolean;
  /** ⭐ UI v2 步 8（`decisions/052`）：速度／音量（設計 C11） */
  speed: number;
  volume: number;
  cycleRepeat: () => void;
  toggleShuffle: () => void;
  cycleSpeed: () => void;
  setVolume: (v: number) => void;
  /** 開始播（可以順便交一份歌單） */
  play: (track: Track, queue?: Track[]) => void;
  toggle: () => void;
  seek: (ms: number) => void;
  next: () => void;
  prev: () => void;
  setRepeat: (mode: RepeatMode) => void;
  setShuffle: (on: boolean) => void;
  /**
   * ⭐ **用戶改歌名**（`decisions/039`；2026-09-25）—— 更新**而家播緊嗰首**（＋queue 內對應項）嘅標題。
   *   為何要：`Track.title` 係 `play()` 嗰刻**snapshot** → 唔更新嘅話，底部常駐播放條／鎖屏（MediaSession）
   *   會一直顯示**舊名**（產品負責人問「清單會唔會跟」時一齊處理）。
   *   ⚠️ 只改標題字串（唔碰 audio src／播放位置／播放狀態）
   */
  renameTrack: (id: string, title: string) => void;
  /** 切換高音質（未有 flac 就做唔到 → 回 false） */
  setHifi: (on: boolean) => boolean;
}

const PlayerContext = createContext<PlayerApi | null>(null);

/** ⭐ UI v2 步 8：播放速度段（設計 C11 §12 R-12：4 段） */
const SPEEDS = [0.75, 1, 1.25, 1.5];

export function PlayerProvider({ children }: { children: ReactNode }) {
  const elRef = useRef<HTMLAudioElement | null>(null);
  const [track, setTrack] = useState<Track | null>(null);
  const [queue, setQueue] = useState<Track[]>([]);
  const [playing, setPlaying] = useState(false);
  const [positionMs, setPositionMs] = useState(0);
  const [durationMs, setDurationMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hifi, setHifiState] = useState(false);
  /** ⭐ 2026-09-24：播放模式（單曲／全部循環、隨機） */
  const [repeat, setRepeatState] = useState<RepeatMode>('off');
  const [shuffle, setShuffleState] = useState(false);
  /** ⭐ UI v2 步 8：速度（0.75／1／1.25／1.5）＋ 音量（0–100） */
  const [speed, setSpeed] = useState(1);
  const [volume, setVolume] = useState(100);

  /**
   * ⭐ **順播（`decisions/033`）**：`ended` handler 只會建立一次，但佢要讀**最新**嘅
   *   queue／track／load／播放模式 → 用一個 ref 裝住（唔用 ref 就會捉到開頭嘅空值）。
   */
  const latest = useRef<{
    queue: Track[];
    track: Track | null;
    load: (t: Track, autoplay: boolean) => void;
    repeat: RepeatMode;
    shuffle: boolean;
  }>({
    queue: [],
    track: null,
    load: () => {},
    repeat: 'off',
    shuffle: false,
  });

  /** 第一次要用才建立（唔會喺頁面載入時就抓檔案） */
  const el = useCallback((): HTMLAudioElement => {
    if (!elRef.current) {
      const a = new Audio();
      a.preload = 'metadata';
      a.addEventListener('timeupdate', () => setPositionMs(a.currentTime * 1000));
      a.addEventListener('durationchange', () => setDurationMs(Number.isFinite(a.duration) ? a.duration * 1000 : null));
      a.addEventListener('play', () => setPlaying(true));
      a.addEventListener('pause', () => setPlaying(false));
      /**
       * ⭐ **順播到尾**（`decisions/033`；產品負責人 2026-09-24）：
       *   一首播完 → 有下一首就自動接落去；**到尾就停**（唔循環 —— 同手動〔下一首〕唔同）。
       *   ⚠️ 唔可以喺呢度只 `setPlaying(false)` 就算：咁樣「播清單」永遠只播到第一首。
       */
      a.addEventListener('ended', () => {
        setPlaying(false);
        const { queue: q, track: cur, load: doLoad, repeat: rp, shuffle: sh } = latest.current;
        if (!cur) return;
        /** ⭐ **單曲循環**：即刻由頭再播同一首（產品負責人：「repeat one」） */
        if (rp === 'one') {
          a.currentTime = 0;
          void a.play().catch(() => setPlaying(false));
          return;
        }
        if (q.length <= 1) return;
        const i = q.findIndex((t) => t.id === cur.id);
        /**
         * ⭐ **順播／隨機／全部循環**（產品負責人 2026-09-24）：
         *   `pickNextIndex()` 一次過處理「下一首／隨機／到尾」；回 `-1` ＝ **冇下一首 → 停**
         */
        const next = pickNextIndex(q.length, i < 0 ? 0 : i, { repeat: rp, shuffle: sh });
        if (next < 0) return;
        const target = q[next];
        if (target) doLoad(target, true);
      });
      a.addEventListener('error', () => {
        /** 原樣報錯（唔准靜靜） */
        setError(`audio_error${a.error?.code ? `（${a.error.code}）` : ''}`);
        setPlaying(false);
      });
      elRef.current = a;
    }
    return elRef.current;
  }, []);

  const load = useCallback(
    (t: Track, autoplay: boolean) => {
      const a = el();
      setTrack(t);
      setError(null);
      setPositionMs(0);
      setDurationMs(t.durationMs);
      a.src = t.audioSrc;
      if (autoplay) {
        void a.play().catch((err) => setError(`play_blocked（${String(err).slice(0, 60)}）`));
      }
    },
    [el],
  );

  /** ⭐ UI v2 步 8：速度／音量要套落 audio element（同一個 element → 換歌唔會走樣） */
  useEffect(() => {
    const a = el();
    a.playbackRate = speed;
    a.volume = Math.min(1, Math.max(0, volume / 100));
  }, [el, speed, volume]);

  /** ⭐ 播放模式 ＋ queue：render 之後更新（`ended` handler 靠佢讀最新值） */
  useEffect(() => {
    latest.current = { queue, track, load, repeat, shuffle };
  }, [load, queue, repeat, shuffle, track]);

  const play = useCallback(
    (t: Track, list?: Track[]) => {
      if (list) setQueue(list);
      load(t, true);
    },
    [load],
  );

  const toggle = useCallback(() => {
    const a = el();
    if (!track) return;
    if (a.paused) void a.play().catch((err) => setError(`play_blocked（${String(err).slice(0, 60)}）`));
    else a.pause();
  }, [el, track]);

  const seek = useCallback(
    (ms: number) => {
      const a = el();
      const target = clampSeek(ms, durationMs);
      a.currentTime = target / 1000;
      setPositionMs(target);
    },
    [durationMs, el],
  );

  /** ⭐ 手動／耳機〔下一首〕〔上一首〕：跟播放模式（隨機／全部循環） */
  const go = useCallback(
    (delta: number) => {
      if (queue.length === 0 || !track) return;
      const i = queue.findIndex((t) => t.id === track.id);
      const cur = i < 0 ? 0 : i;
      const target =
        delta > 0
          ? queue[pickNextIndex(queue.length, cur, { repeat, shuffle })]
          : queue[pickPrevIndex(queue.length, cur, { shuffle })];
      if (target) load(target, true);
    },
    [load, queue, repeat, shuffle, track],
  );

  /** ⭐ UI v2 步 8（設計 C11）：播咗超過 3 秒 → **返歌頭**；否則上一首 */
  const prev = useCallback(() => {
    if (positionMs > 3000) {
      seek(0);
      return;
    }
    go(-1);
  }, [go, positionMs, seek]);

  /** ⭐ 循環模式：off → all → one（單一掣循環；`decisions/052` 步 8） */
  const cycleRepeat = useCallback(() => setRepeatState((r) => (r === 'off' ? 'all' : r === 'all' ? 'one' : 'off')), []);
  const toggleShuffle = useCallback(() => setShuffleState((s) => !s), []);
  /** 速度：0.75 → 1 → 1.25 → 1.5 → 返 0.75 */
  const cycleSpeed = useCallback(() => setSpeed((s) => SPEEDS[(SPEEDS.indexOf(s) + 1) % SPEEDS.length]), []);

  const setHifi = useCallback(
    (on: boolean): boolean => {
      const a = el();
      const wasPlaying = !a.paused;
      const at = a.currentTime;
      const src = on ? track?.hifiSrc : track?.audioSrc;
      /** 未有 flac → 做唔到（**唔准**扮切咗） */
      if (!src) return false;
      setHifiState(on);
      a.src = src;
      a.currentTime = at;
      if (wasPlaying) void a.play().catch(() => setPlaying(false));
      return true;
    },
    [el, track],
  );

  /** Media Session（鎖屏／耳機掣；唔支援就靜靜略過 —— 唔係功能缺失）
   *  ⚠️ 依賴 `track` → `renameTrack()` 改完標題會**自動更新鎖屏**（唔使另外做嘢） */
  useEffect(() => {
    const ms = (navigator as unknown as { mediaSession?: MediaSession }).mediaSession;
    if (!ms || !track) return;
    try {
      ms.metadata = new MediaMetadata({ title: track.title, artist: 'CoSingery' });
      ms.setActionHandler('play', () => toggle());
      ms.setActionHandler('pause', () => toggle());
      ms.setActionHandler('nexttrack', () => go(1));
      ms.setActionHandler('previoustrack', () => go(-1));
      ms.setActionHandler('seekto', (d) => {
        if (typeof d.seekTime === 'number') seek(d.seekTime * 1000);
      });
    } catch {
      /** 個別瀏覽器唔支援某啲 action → 略過 */
    }
  }, [go, seek, toggle, track]);

  /**
   * ⭐ **改歌名**（`decisions/039`）：`track` 同 `queue` 一齊換（唔碰 audio／位置／播放狀態）
   *   → 底部播放條、播放器頁、鎖屏（MediaSession metadata 依賴 `track`）即刻一致
   */
  const renameTrack = useCallback((id: string, title: string) => {
    setQueue((q) => q.map((t) => (t.id === id ? { ...t, title } : t)));
    setTrack((t) => (t && t.id === id ? { ...t, title } : t));
  }, []);

  const api = useMemo<PlayerApi>(
    () => ({
      track,
      queue,
      playing,
      positionMs,
      durationMs,
      error,
      hifi,
      repeat,
      shuffle,
      speed,
      volume,
      cycleRepeat,
      toggleShuffle,
      cycleSpeed,
      setVolume,
      play,
      toggle,
      seek,
      next: () => go(1),
      prev,
      setRepeat: setRepeatState,
      setShuffle: setShuffleState,
      renameTrack,
      setHifi,
    }),
    [
      cycleRepeat,
      cycleSpeed,
      durationMs,
      error,
      go,
      hifi,
      play,
      playing,
      positionMs,
      prev,
      queue,
      renameTrack,
      repeat,
      seek,
      setHifi,
      shuffle,
      speed,
      toggle,
      toggleShuffle,
      track,
      volume,
    ],
  );

  return <PlayerContext.Provider value={api}>{children}</PlayerContext.Provider>;
}

export function usePlayer(): PlayerApi {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer 要喺 <PlayerProvider> 之內用');
  return ctx;
}
